import { Link } from "react-router-dom";
import { SectionLabel, Headline, Stamp } from "@/components/Doc";

const steps = [
  { title: "Scoping call", text: "Forty minutes with the sponsor. What can one credential reach, who can move money, what has already gone wrong. We decide together whether a single module or the full install is the right shape." },
  { title: "The audit, across the team", text: "The Key Ring Audit is run not by one person but by everyone who will be in the room. The spread between answers is usually the most useful finding, and it sets the agenda for the two days.", link: { to: "/team", label: "Open a team audit →" } },
  { title: "Two days on site", text: "In your building, with your people, without laptops. Drills in the morning, writing in the afternoon. The artifacts are made in the room and the changes are argued out before anyone leaves." },
  { title: "The closing memorandum", text: "One document listing every installed change, with an owner and a date against each, signed by the sponsor. It is the contract the team makes with itself." },
  { title: "Ninety-day review", text: "We come back. We read the memorandum aloud and ask, line by line, whether it held. What held is filed. What didn't is fixed or formally dropped. Either way, it's written down." },
];

export default function Method() {
  return (
    <main className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="method-page">
      <SectionLabel right="Form LH-05">Doc. 05 — The Method</SectionLabel>
      <Headline as="h1" className="mt-10 text-4xl sm:text-5xl lg:text-6xl">How an engagement works.</Headline>
      <p className="t-body text-lg sm:text-xl mt-6 max-w-[60ch] leading-[1.65]">Five steps. Every one of them ends with something written down.</p>

      <div className="mt-16 flex flex-col">
        {steps.map((s, i) => (
          <div key={s.title} className="grid sm:grid-cols-12 gap-4 py-8 border-t border-rule" data-testid={`method-step-${i + 1}`}>
            <span className="t-label sm:col-span-2">Step {String(i + 1).padStart(2, "0")}</span>
            <div className="sm:col-span-10">
              <h3 className="t-head text-3xl sm:text-4xl">{s.title}</h3>
              <p className="t-body text-lg sm:text-xl mt-4 max-w-[65ch] leading-[1.65]">{s.text}</p>
              {s.link && <Link to={s.link.to} className="t-label inline-block mt-4 text-carbon hover:text-stamp" data-testid="method-team-link">{s.link.label}</Link>}
            </div>
          </div>
        ))}
      </div>

      <section className="mt-24 border-t border-carbon pt-16" data-testid="engagement-shapes">
        <SectionLabel>Doc. 06 — Engagement Shapes</SectionLabel>
        <div className="grid md:grid-cols-2 gap-8 mt-12">
          <div className="bg-manila border border-rule p-8 relative">
            <span className="t-label text-olive">Shape A</span>
            <h3 className="t-head text-3xl mt-2">A single module</h3>
            <p className="t-body text-lg mt-4 leading-[1.65]">The usual entry point. Most companies begin with Module I, because the audit almost always points there first. Two days, one cohort, one closing memorandum, one review.</p>
          </div>
          <div className="bg-paper border border-carbon p-8 relative shadow-[6px_6px_0_0_#D9C9A8]">
            <div className="absolute top-4 right-4"><Stamp color="red" rotate={5} size="sm">Recommended</Stamp></div>
            <span className="t-label text-olive">Shape B</span>
            <h3 className="t-head text-3xl mt-2">The full install</h3>
            <p className="t-body text-lg mt-4 leading-[1.65]">All four modules across a quarter, one per month, each cohort chosen for the module. The company finishes with the Register, the Log, the Standard and the Matrix, and a single ninety-day review that reads all four memoranda together.</p>
          </div>
        </div>
        <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6">
          <Link to="/pricing" className="btn btn-stamp" data-testid="request-memorandum-btn">See the Schedule of Fees</Link>
          <Link to="/contact?interest=memorandum" className="t-label text-carbon hover:text-stamp" data-testid="request-memorandum-link">Request the Engagement Memorandum →</Link>
          <span className="t-label text-olive">Scope, cohort, sequence and terms. No prices published.</span>
        </div>
      </section>
    </main>
  );
}
